import { Button, Checkbox, DialogActions, DialogContent, Divider, FormControlLabel, Link, Typography, useColorScheme } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import { Stack, useMediaQuery } from '@mui/system';
import Lottie from 'lottie-react';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { robotDataSlice } from '../../../redux/RobotDataSlice';
import { robotSlice } from '../../../redux/RobotSlice';
import { RootState } from '../../../redux/Store';
import { trackConnectSelected } from '../../../analytics/bpsEvents';
import { BLUEPRINT_STUDIO_CONNECTION_HELP_URL, RobotActions, RobotArray, ROBOTS } from '../../../utils/constants';
import { useConnectionPreferences } from '../../../hooks/storage/useConnectionPreferences';
import BPRBluetoothConnectionSvg from '../../Icons/Connection/bpr-bluetooth-connection.svg';
import btConnectionLottieDark from '../../Icons/Connection/bps-bt-connection-dark.json';
import btConnectionLottieLight from '../../Icons/Connection/bps-bt-connection-light.json';

interface RobotConnectInfoDialogProps {
  onClose: () => void;
}

const RobotConnectInfoDialog = ({ onClose }: RobotConnectInfoDialogProps) => {
  const { mode, systemMode } = useColorScheme();
  let colorMode = mode;
  if (mode === 'system') {
    colorMode = systemMode;
  }
  const { userRobotColor } = useSelector((state: RootState) => state.robotData);
  const { selectedRobot } = useSelector((state: RootState) => state.application.openDialog.options);
  const dispatch = useDispatch();
  const [connectionPreferences, setConnectionPreferences] = useConnectionPreferences();

  const fullRobotName = ROBOTS.BPRDU.name;
  const [imageHeight, setImageHeight] = useState<string>('160');
  const [animationHeight, setAnimationHeight] = useState<string>('256');
  // for devices like chromebooks that have wide but short screen ratios.
  const shortScreen = useMediaQuery(`(max-height: 700px)`);

  useEffect(() => {
    if (shortScreen) {
      setImageHeight('68');
      setAnimationHeight('109');
    } else {
      setImageHeight('160');
      setAnimationHeight('256');
    }
  }, [shortScreen]);

  const getSelectedRobot = () => {
    const selectedRobotType = selectedRobot;
    return RobotArray.find((robot) => robot.type === selectedRobotType);
  };

  const connectRobot = () => {
    const color = userRobotColor;
    trackConnectSelected(RobotActions.STATE.CONNECTION_TYPE.BLE);
    dispatch(robotDataSlice.actions.onUpdateRobotType(getSelectedRobot()));
    dispatch(
      robotSlice.actions.onUpdateRobotStateAndConnectionType({
        robotState: RobotActions.STATE.CONNECT,
        connectionType: RobotActions.STATE.CONNECTION_TYPE.BLE,
      })
    );
    dispatch(robotDataSlice.actions.onUpdateUserRobotColor(color));
  };

  const handleClose = () => {
    onClose();
  };

  const handleDontShowAgainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setConnectionPreferences({
      ...connectionPreferences,
      skipBluetoothInfo: checked,
    });
  };

  return (
    <>
      <DialogTitle textAlign="center" variant="panelTitle" sx={{ py: 1.5 }}>
        <Trans i18nKey="robotConnectOption.title">Wireless Connection</Trans>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: shortScreen ? 1 : 2, px: 2.5 }}>
        <Stack direction="column" spacing={2} alignItems="center">
          <Typography variant="body1">
            <Trans i18nKey={'robotConnection.bt.heading'}>Power on your {fullRobotName} and keep it in range of your computer.</Trans>
          </Typography>
          <img
            src={BPRBluetoothConnectionSvg}
            alt="Swerve Drive Robot Wireless Connection"
            height={imageHeight}
            style={{ filter: colorMode === 'dark' ? 'invert(1)' : 'none' }}
          />
          <Typography variant="body1">
            <Trans i18nKey={'robotConnection.bt.second_heading'}>
              Press the {"'Connect'"} button below to start pairing. Then select your {fullRobotName} and choose {"'Pair'"}
            </Trans>
          </Typography>
          <Lottie
            animationData={colorMode === 'dark' ? btConnectionLottieDark : btConnectionLottieLight}
            loop
            style={{ height: `${animationHeight}px` }}
          />
          <Stack spacing={0.5} alignItems="center">
            <Typography variant="body1">
              <Trans i18nKey={'robotConnection.having_issues'}>Having issues connecting your {fullRobotName}?</Trans>
            </Typography>
            <Link href={BLUEPRINT_STUDIO_CONNECTION_HELP_URL} target="_blank" rel="noopener noreferrer">
              <Trans i18nKey="robotConnection.learn_more">Learn More</Trans>
            </Link>
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2 }}>
        <FormControlLabel
          sx={{
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            bgcolor: 'action.hover',
            '& .MuiFormControlLabel-label': {
              fontSize: 13,
            },
          }}
          control={
            <Checkbox
              color="primary"
              size="small"
              sx={{ p: 0.5, borderRadius: 1 }}
              checked={connectionPreferences.skipBluetoothInfo}
              onChange={handleDontShowAgainChange}
            />
          }
          label={<Trans i18nKey="robotConnection.dont_show_again">Don't show this again</Trans>}
        />
        <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
          <Button variant="text" autoFocus onClick={handleClose}>
            <Trans i18nKey="button.cancel">Cancel</Trans>
          </Button>
          <Button variant="contained" onClick={() => connectRobot()} autoFocus>
            <Trans i18nKey="button.connect">Connect</Trans>
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
};

export default RobotConnectInfoDialog;
